import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CalendarClock, Loader2 } from 'lucide-react';
import { Calendar } from '../../../components/ui/calendar';

const WARSAW_TZ = 'Europe/Warsaw';

// ─── Warsaw timezone helpers ─────────────────────────────────────────
// We compute Warsaw's UTC offset for any given Date by abusing
// Intl.DateTimeFormat: format the moment in Warsaw, parse the wall-clock
// components, then express the same wall-clock as UTC and read back the
// delta. This survives CET ↔ CEST transitions without any external lib.

function _warsawParts(date) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: WARSAW_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (t) => parseInt(parts.find((p) => p.type === t)?.value || '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function _warsawOffsetMinutesAt(date) {
  // Difference between "Warsaw wall clock" treated as UTC and the actual
  // UTC time. Positive in summer (CEST = UTC+2 → +120), CET (UTC+1 → +60).
  const w = _warsawParts(date);
  const wallAsUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return (wallAsUtc - date.getTime()) / 60000;
}

// Build a UTC Date from a "wall clock in Warsaw" spec.
function _warsawWallClockToUtcDate(y, m, d, hour, minute) {
  // Anchor on a guess UTC, measure Warsaw's offset there, then correct.
  const guess = new Date(Date.UTC(y, m, d, hour, minute, 0));
  const offset = _warsawOffsetMinutesAt(guess);
  return new Date(guess.getTime() - offset * 60000);
}

// Returns the Warsaw-time abbreviation (CET or CEST) for a Date.
function _warsawAbbrAt(date) {
  return _warsawOffsetMinutesAt(date) === 60 ? 'CET' : 'CEST';
}

// Default scheduled time: Warsaw "now + 1 hour", rounded down to :00.
function _defaultWarsawSchedule() {
  const now = new Date();
  const wNow = _warsawParts(now);
  const candidateHour = (wNow.hour + 1) % 24;
  const dayCarry = wNow.hour + 1 >= 24 ? 1 : 0;
  // Anchor day/month/year via Warsaw view, with carry on hour overflow.
  let y = wNow.year;
  let m = wNow.month; // 1-indexed
  let d = wNow.day + dayCarry;
  const monthDays = new Date(y, m, 0).getDate(); // last day of month m
  if (d > monthDays) { d = 1; m += 1; if (m > 12) { m = 1; y += 1; } }
  return { year: y, month: m - 1, day: d, hour: candidateHour, minute: 0 };
}

function _to12h(hour24) {
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  let h = hour24 % 12;
  if (h === 0) h = 12;
  return { h, ampm };
}

function _from12h(h12, ampm) {
  let h = h12 % 12; // 12 → 0
  if (ampm === 'PM') h += 12;
  return h;
}

// ─── Modal ─────────────────────────────────────────────────────────

export default function ScheduleModal({ open, onClose, onConfirm, busy }) {
  const initial = useMemo(_defaultWarsawSchedule, []);

  // Internal state holds Warsaw wall-clock components (the user is always
  // thinking in Warsaw). On confirm we convert to a real UTC Date and
  // serialize to ISO (the backend stores ISO strings).
  const [date, setDate] = useState(() => new Date(initial.year, initial.month, initial.day));
  const [h12, setH12] = useState(_to12h(initial.hour).h);
  const [minute, setMinute] = useState(initial.minute);
  const [ampm, setAmpm] = useState(_to12h(initial.hour).ampm);

  // Reset whenever the modal opens, so reopening doesn't show stale state.
  useEffect(() => {
    if (!open) return;
    const i = _defaultWarsawSchedule();
    setDate(new Date(i.year, i.month, i.day));
    setH12(_to12h(i.hour).h);
    setMinute(i.minute);
    setAmpm(_to12h(i.hour).ampm);
  }, [open]);

  const selectedUtc = useMemo(() => {
    if (!date) return null;
    const hour24 = _from12h(h12, ampm);
    return _warsawWallClockToUtcDate(date.getFullYear(), date.getMonth(), date.getDate(), hour24, minute);
  }, [date, h12, minute, ampm]);

  const warsawAbbr = useMemo(() => (selectedUtc ? _warsawAbbrAt(selectedUtc) : 'CET'), [selectedUtc]);

  const isInPast = useMemo(() => (selectedUtc ? selectedUtc.getTime() < Date.now() : false), [selectedUtc]);

  const minutes = useMemo(() => [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55], []);

  const handleConfirm = () => {
    if (!selectedUtc || isInPast || busy) return;
    onConfirm(selectedUtc.toISOString(), {
      warsaw_display: formatWarsawHuman(selectedUtc),
      timezone: WARSAW_TZ,
    });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="schedule-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
        onClick={onClose}
        data-testid="dm-schedule-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-[#070707] border border-[rgba(228,192,122,0.45)] w-full max-w-xl flex flex-col shadow-[0_0_60px_rgba(228,192,122,0.08)]"
        >
          <header className="px-6 py-4 border-b border-[#1a1a1a] flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#e4c07a] inline-flex items-center gap-2">
                <CalendarClock className="w-3.5 h-3.5" /> Schedule send
              </div>
              <div className="text-[12px] font-outfit text-[#aaa] mt-1">
                Pick a date and time. We&apos;ll send the campaign automatically.
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="dm-schedule-close"
              className="w-9 h-9 border border-[#222] flex items-center justify-center text-[#777] hover:text-white hover:border-[#444]"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Calendar */}
            <div className="border border-[#1a1a1a] bg-[#050505] p-2 flex justify-center" data-testid="dm-schedule-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return d < today;
                }}
                className="text-white"
              />
            </div>

            {/* Time controls */}
            <div className="space-y-4">
              <div>
                <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-[#777] mb-2">
                  Hour
                </span>
                <div className="flex flex-wrap gap-1.5" data-testid="dm-schedule-hour-grid">
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setH12(n)}
                      data-testid={`dm-hour-${n}`}
                      className={`w-9 h-9 text-[12px] font-mono border transition-all ${
                        h12 === n
                          ? 'border-[#e4c07a] bg-[rgba(228,192,122,0.12)] text-[#e4c07a]'
                          : 'border-[#222] text-[#aaa] hover:border-[#444] hover:text-white'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-[#777] mb-2">
                  Minute
                </span>
                <div className="flex flex-wrap gap-1.5" data-testid="dm-schedule-minute-grid">
                  {minutes.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setMinute(n)}
                      data-testid={`dm-minute-${String(n).padStart(2, '0')}`}
                      className={`w-9 h-9 text-[12px] font-mono border transition-all ${
                        minute === n
                          ? 'border-[#e4c07a] bg-[rgba(228,192,122,0.12)] text-[#e4c07a]'
                          : 'border-[#222] text-[#aaa] hover:border-[#444] hover:text-white'
                      }`}
                    >
                      {String(n).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-[#777] mb-2">
                  Period
                </span>
                <div className="grid grid-cols-2 gap-1.5" data-testid="dm-schedule-ampm">
                  {['AM', 'PM'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAmpm(p)}
                      data-testid={`dm-period-${p.toLowerCase()}`}
                      className={`h-10 text-[12px] font-mono uppercase tracking-[0.18em] border transition-all ${
                        ampm === p
                          ? 'border-[#e4c07a] bg-[rgba(228,192,122,0.12)] text-[#e4c07a]'
                          : 'border-[#222] text-[#aaa] hover:border-[#444] hover:text-white'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-[#1a1a1a] bg-[#050505] px-3 py-2.5" data-testid="dm-schedule-preview">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777]">
                  Will send at
                </div>
                <div className="mt-1 text-[14px] font-outfit text-white">
                  {selectedUtc ? formatWarsawHuman(selectedUtc) : '—'}
                </div>
                <div className="mt-1 text-[10px] font-mono text-[#888]">
                  Warsaw time · {warsawAbbr} (UTC{warsawAbbr === 'CEST' ? '+2' : '+1'})
                </div>
                {isInPast && (
                  <div className="mt-1 text-[10px] font-mono text-[#e88ba0]" data-testid="dm-schedule-past-warning">
                    The selected time is already in the past.
                  </div>
                )}
              </div>
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              data-testid="dm-schedule-cancel"
              className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444]"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedUtc || isInPast || busy}
              data-testid="dm-schedule-confirm"
              className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border border-[rgba(228,192,122,0.55)] text-[#e4c07a] hover:bg-[rgba(228,192,122,0.08)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              {busy ? 'Scheduling…' : 'Confirm schedule'}
            </button>
          </footer>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Pretty-print a Date in Warsaw time, e.g. "Sat 28 Jun 2026 · 17:00".
function formatWarsawHuman(d) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: WARSAW_TZ,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  // Build "Sat 28 Jun 2026 · 17:00"
  const parts = fmt.formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')} · ${get('hour')}:${get('minute')}`;
}
